import { useSearchParams } from '@remix-run/react'
import { type PaginationState, type SortingState } from '@tanstack/react-table'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type PaginateData } from '#app/shared/lib/find-criteria.js'
import { withoutNullKeyValues } from '#app/shared/lib/objects.js'

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
) {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

type DataTableProps<T extends { [key: string]: any } | undefined> = {
  filtersData?: T
  sortData: any
  paginateData?: PaginateData
  defaultQuery?: string
}

const buildPagination = (paginateData: any) => {
  return {
    pageIndex: Math.max(paginateData.page - 1, 0),
    pageSize: paginateData.perPage,
  }
}

const areThereFilters = (filtersData: any) => {
  return filtersData && Object.keys(filtersData).length > 0
}

const buildFilters = (filtersData: any) => {
  if (areThereFilters(filtersData)) {
    return withoutNullKeyValues(filtersData as any, {})
  }
  return undefined
}

const buildSorting = (sortData: any) => {
  return sortData.sortBy
    ? [{ id: sortData.sortBy, desc: sortData.reverse }]
    : []
}

function useDataTable<T extends { [key: string]: any } | undefined>({
  filtersData,
  sortData,
  paginateData,
}: DataTableProps<T>) {
  const [, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState<T>(buildFilters(filtersData) as T)
  const [sorting, setSorting] = useState<SortingState>(buildSorting(sortData))
  const [pagination, setPagination] = useState<PaginationState>(
    paginateData
      ? buildPagination(paginateData)
      : {
          pageIndex: 0,
          pageSize: 10,
        },
  )

  const isInitialRender = useRef(0)

  const updateQueryParams = useCallback(
    (
      newSorting: SortingState,
      newPagination: PaginationState,
      newFilters: T | undefined,
    ) => {
      const params = new URLSearchParams()

      if (newSorting.length > 0 && newSorting[0]?.id != null) {
        params.set('sortBy', newSorting[0].id)
        params.set('reverse', newSorting[0].desc ? 'true' : 'false')
      } else {
        params.delete('sortBy')
        params.delete('reverse')
      }
      params.set('page', newPagination.pageIndex.toString())
      params.set('perPage', newPagination.pageSize.toString())
      if (newFilters) {
        Object.keys(newFilters).forEach((key) => {
          params.set(key, newFilters[key])
        })
      }

      return params
    },
    [],
  )

  const getQueryString = useCallback(() => {
    return updateQueryParams(sorting, pagination, filters).toString()
  }, [sorting, pagination, filters, updateQueryParams])

  //TODO: Fix this eslint

  const debouncedUpdateSearchParams = useCallback(
    debounce(() => {
      setSearchParams(updateQueryParams(sorting, pagination, filters), {
        preventScrollReset: true,
        replace: true,
      })
    }, 300),
    [sorting, pagination, filters, updateQueryParams, setSearchParams],
  )

  useEffect(() => {
    if (isInitialRender.current < 1) {
      isInitialRender.current += 1
      return
    }
    debouncedUpdateSearchParams()
  }, [sorting, pagination, filters])

  return {
    sorting,
    setSorting,
    filters,
    setFilters,
    pagination,
    setPagination,
    queryString: getQueryString(),
  }
}

export default useDataTable
